import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { supabase } from '../lib/supabase';
import { useTranslation } from 'react-i18next';

const TUTORIAL_STEPS = [
  { id: 'nav-item-home', key: 'marc_tutorial.step_home' },
  { id: 'nav-item-calendar', key: 'marc_tutorial.step_calendar' },
  { id: 'nav-item-workout', key: 'marc_tutorial.step_workout' },
  { id: 'nav-item-diet', key: 'marc_tutorial.step_diet' },
  { id: 'nav-item-milestones', key: 'marc_tutorial.step_milestones' },
  { id: 'nav-item-chat', key: 'marc_tutorial.step_chat' }
];

export const MarcTutorialOverlay: React.FC = () => {
  const { user, loading, marcAvatarUrl } = useAuth();
  const { t } = useTranslation();
  const location = useLocation();
  const [isVisible, setIsVisible] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);
  const [targetRect, setTargetRect] = useState<DOMRect | null>(null);

  useEffect(() => {
    if (loading) return;
    if (location.pathname !== '/dashboard') return;

    const isSeen = () => {
      if (localStorage.getItem('aura_tutorial_completed')) return true;
      try {
        const ud = JSON.parse(localStorage.getItem('aura_user_data') || '{}');
        if (ud?.aura_tutorial_completed) {
          localStorage.setItem('aura_tutorial_completed', 'true');
          return true;
        }
      } catch (_) {}
      return false;
    };

    if (isSeen()) return;

    let cancelled = false;
    let poller: NodeJS.Timeout;
    let timer: NodeJS.Timeout;

    const startTutorial = () => {
      const checkReady = () => {
        const el = document.getElementById(TUTORIAL_STEPS[0].id);
        if (el) {
          clearInterval(poller);
          timer = setTimeout(() => {
            if (!cancelled) setIsVisible(true);
          }, 1500);
        }
      };
      poller = setInterval(checkReady, 200);
    };

    const checkViaSupabase = async () => {
      if (!user) {
        // Small delay to avoid flashing before auth resolves
        await new Promise(r => setTimeout(r, 1500));
        if (cancelled) return;
        if (!isSeen()) startTutorial();
        return;
      }

      try {
        const { data, error } = await supabase
          .from('profiles')
          .select('stats')
          .eq('id', user.id)
          .single();

        if (cancelled) return;

        if (error) {
          console.warn('[MarcTutorial] Could not verify tutorial status from DB:', error.message);
          return;
        }

        let stats = data?.stats;
        if (typeof stats === 'string') {
          try { stats = JSON.parse(stats); } catch (_) { stats = {}; }
        }
        stats = stats || {};

        if (stats.aura_tutorial_completed) {
          localStorage.setItem('aura_tutorial_completed', 'true');
          return; // Skip
        }

        startTutorial();
      } catch (e) {
        if (cancelled) return;
        console.warn('[MarcTutorial] Exception during DB check:', e);
      }
    };

    checkViaSupabase();

    return () => {
      cancelled = true;
      clearInterval(poller);
      clearTimeout(timer);
    };
  }, [location.pathname, loading, user]);

  useEffect(() => {
    if (!isVisible) return;
    
    // Calculate position
    const updatePosition = () => {
      const step = TUTORIAL_STEPS[currentStep];
      const el = document.getElementById(step.id);
      if (el) {
        setTargetRect(el.getBoundingClientRect());
      } else {
        setTargetRect(null);
      }
    };
    
    updatePosition();
    window.addEventListener('resize', updatePosition);
    window.addEventListener('scroll', updatePosition, true);
    
    // Polling backup just in case DOM paints late or images load
    const interval = setInterval(updatePosition, 500);
    
    return () => {
      window.removeEventListener('resize', updatePosition);
      window.removeEventListener('scroll', updatePosition, true);
      clearInterval(interval);
    };
  }, [currentStep, isVisible]);

  const handleNext = async () => {
    if (currentStep < TUTORIAL_STEPS.length - 1) {
      setCurrentStep(prev => prev + 1);
    } else {
      setIsVisible(false);
      localStorage.setItem('aura_tutorial_completed', 'true');
      
      if (user) {
        try {
          const { data } = await supabase.from('profiles').select('stats').eq('id', user.id).single();
          if (data) {
            let stats = typeof data.stats === 'string' ? JSON.parse(data.stats) : (data.stats || {});
            stats = { ...stats, aura_tutorial_completed: true };
            await supabase.from('profiles').update({ stats }).eq('id', user.id);
            
            const localDataRaw = localStorage.getItem('aura_user_data');
            if (localDataRaw) {
               let localData = JSON.parse(localDataRaw);
               if (typeof localData === 'string') localData = JSON.parse(localData);
               localData.aura_tutorial_completed = true;
               localStorage.setItem('aura_user_data', JSON.stringify(localData));
            }
          }
        } catch (e) {
          console.error("Error saving tutorial completion:", e);
        }
      }
    }
  };

  const formatText = (text: string) => {
    const parts = text.split(/(\(Solo PRO\)|\b[Pp][Rr][Oo]\b)/g);

    return (
      <>
        {parts.map((part, i) => {
          if (part.toUpperCase() === '(SOLO PRO)' || part.toUpperCase() === 'PRO') {
            return (
              <span key={i} className="inline-flex items-center justify-center text-primary font-black uppercase tracking-wider text-[11px] bg-primary/15 px-2 py-0.5 rounded-md mx-1 border border-primary/30 align-middle shadow-[0_0_10px_rgba(242,127,13,0.2)]">
                <span className="material-symbols-outlined text-[12px] mr-1">star</span>
                PRO
              </span>
            );
          }
          return <React.Fragment key={i}>{part}</React.Fragment>;
        })}
      </>
    );
  };

  if (!isVisible) return null;

  return (
    <div 
      className="fixed inset-0 z-[10020] overflow-hidden touch-none"
      onClick={handleNext}
    >
      {/* Safari-Safe Box-Shadow Spotlight */}
      {targetRect && (
        <motion.div 
          initial={{ opacity: 0 }}
          animate={{ 
            opacity: 1,
            left: targetRect.left + targetRect.width / 2, 
            top: targetRect.top + targetRect.height / 2, 
            width: Math.max(targetRect.width, targetRect.height) * 1.2 + 5,
            height: Math.max(targetRect.width, targetRect.height) * 1.2 + 5,
            x: "-50%",
            y: "-50%"
          }}
          transition={{ type: "spring", stiffness: 300, damping: 25 }}
          className="absolute rounded-[40px] pointer-events-none"
          style={{ 
            boxShadow: '0 0 0 4000px rgba(0,0,0,0.88), inset 0 0 20px rgba(242,127,13,0.3)',
            border: '2.5px solid #F27F0D',
            filter: 'drop-shadow(0 0 10px rgba(242,127,13,0.8))'
          }}
        />
      )}

      {/* Marc Avatar and Speech Bubble */}
      {targetRect && (
        <AnimatePresence mode="wait">
          <motion.div
            key={currentStep}
            initial={{ opacity: 0, y: 20, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -10, scale: 0.95 }}
            transition={{ type: "spring", stiffness: 400, damping: 25 }}
            className="absolute left-0 right-0 mx-auto z-[10022] pointer-events-none flex flex-col items-center w-[90%] max-w-[340px]"
            style={{ 
              bottom: window.innerHeight - targetRect.top + 80 
            }}
          >
            {/* Speech Bubble */}
            <div className="bg-white text-black p-5 rounded-3xl rounded-br-md shadow-2xl relative mb-4 w-full border border-white/20 pointer-events-auto">
              <p className="text-[14.5px] font-bold leading-relaxed whitespace-pre-wrap">
                {formatText(t(TUTORIAL_STEPS[currentStep].key))}
              </p>
              {/* Bubble Tail pointing to avatar */}
              <div className="absolute -bottom-4 right-10 w-0 h-0 border-l-[16px] border-l-transparent border-t-[20px] border-t-white border-r-[16px] border-r-transparent drop-shadow-md"></div>
            </div>

            {/* Avatar */}
            <div className="size-24 rounded-full border-[3px] border-white shadow-[0_10px_30px_rgba(0,0,0,0.5)] overflow-hidden self-end mr-4 bg-[#0a0a0c]">
              <img 
                src={marcAvatarUrl || "https://tjteqcrvxtxrvryzssrt.supabase.co/storage/v1/object/public/system-assets/marc-avatar.png"} 
                alt="Marc" 
                className="w-full h-full object-cover object-top" 
                onError={(e) => {
                  (e.target as HTMLImageElement).src = 'https://tjteqcrvxtxrvryzssrt.supabase.co/storage/v1/object/public/system-assets/marc-avatar.png';
                }}
              />
            </div>
          </motion.div>
        </AnimatePresence>
      )}

      {/* Animated Arrow Pointing to Target (Root Level for Coordinate Precision) */}
      {targetRect && (
        <AnimatePresence>
          <motion.div 
            key={`arrow-${currentStep}`}
            initial={{ y: -10, x: "-50%", opacity: 0 }}
            animate={{ y: 0, x: "-50%", opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ y: { repeat: Infinity, duration: 0.6, repeatType: 'reverse', ease: "easeInOut" } }}
            className="absolute z-[10024] flex flex-col items-center justify-center pointer-events-none"
            style={{
              left: targetRect.left + targetRect.width / 2,
              top: targetRect.top - 55 // Hover exactly 55px above the spotlight target
            }}
          >
            <svg width="36" height="36" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" className="text-primary drop-shadow-[0_0_12px_rgba(242,127,13,1)]">
              <path d="M12 3L12 21M12 21L5 14M12 21L19 14" stroke="currentColor" strokeWidth="3.5" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </motion.div>
        </AnimatePresence>
      )}

      {/* Instruction text */}
      <motion.div 
        initial={{ opacity: 0 }}
        animate={{ opacity: 0.8 }}
        transition={{ delay: 1.5 }}
        className="absolute top-24 w-full flex justify-center pointer-events-none z-[10021]"
      >
        <div className="bg-black/70 px-6 py-3 rounded-full border border-white/20 backdrop-blur-md flex items-center gap-2 shadow-[0_0_20px_rgba(0,0,0,0.5)]">
            <span className="material-symbols-outlined text-[16px] text-white">touch_app</span>
            <span className="text-white text-[11px] uppercase tracking-widest font-black">
                Toca la pantalla para avanzar
            </span>
        </div>
      </motion.div>
    </div>
  );
};
